// =====================================================
// 白板系统 - 状态管理 (Zustand)
// =====================================================

import { create } from 'zustand';
import * as service from '../services/whiteboard-service';
import type { WhiteboardState, CreateWhiteboardParams, UpdateWhiteboardParams } from '../types';

export const useWhiteboardStore = create<WhiteboardState>((set, get) => ({
  projects: [],
  collabProjects: [],
  isLoadingProjects: false,
  currentProject: null,
  isLoadingCanvas: false,
  isSidebarOpen: true,

  hasUnsavedChanges: false,
  setHasUnsavedChanges: (v: boolean) => set({ hasUnsavedChanges: v }),
  saveCurrentCanvas: null,
  setSaveCurrentCanvas: (fn) => set({ saveCurrentCanvas: fn }),
  getCanvasExportData: null,
  setGetCanvasExportData: (fn) => set({ getCanvasExportData: fn }),

  loadProjects: async () => {
    set({ isLoadingProjects: true });
    try {
      const [projects, collabProjects] = await Promise.all([
        service.fetchProjects(),
        service.fetchCollabProjects(),
      ]);
      set({ projects, collabProjects });
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      set({ isLoadingProjects: false });
    }
  },

  openProject: async (id: string, shareToken?: string | null) => {
    set({ isLoadingCanvas: true });
    try {
      const project = await service.fetchProjectById(id, shareToken);
      set({ currentProject: project });
    } catch (error) {
      console.error('Error opening project:', error);
    } finally {
      set({ isLoadingCanvas: false });
    }
  },

  createProject: async (params?: CreateWhiteboardParams) => {
    const project = await service.createProject(params);
    const { projects } = get();
    set({ projects: [project, ...projects] });
    return project;
  },

  updateProjectMeta: async (id: string, params: UpdateWhiteboardParams) => {
    const updated = await service.updateProject(id, params);
    const { projects, currentProject } = get();
    set({
      projects: projects.map((p) => (p.id === id ? updated : p)),
      currentProject: currentProject?.id === id ? updated : currentProject,
    });
  },

  deleteProject: async (id: string) => {
    await service.deleteProject(id);
    const { projects, currentProject } = get();
    set({
      projects: projects.filter((p) => p.id !== id),
      currentProject: currentProject?.id === id ? null : currentProject,
    });
  },

  duplicateProject: async (id: string) => {
    const newProject = await service.duplicateProject(id);
    const { projects } = get();
    set({ projects: [newProject, ...projects] });
    return newProject;
  },

  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
  setSidebarOpen: (open: boolean) => set({ isSidebarOpen: open }),
}));
